import React from 'react'

import { Link, Stack, Typography, useTheme } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'

import { InlineEditButton } from '@repo/ui'
// VRG-273 aliased `@repo/types` to source in the platform's vite config, so this is
// a RUNTIME import, not a type-only one. It has to be: the platform and the server
// must answer "may this visitor be asked for their email" with the SAME function,
// not with two implementations that agree until one is edited.
import { AccessSettingSource, resolveAccessSetting } from '@repo/types'

import {
  accessSettingOption,
  type AccessSettingKey,
  type AccessSettingScope as AccessSettingCopyScope,
} from 'constants/accessSettings'
import { useReferrerState } from 'hooks'
import { ROUTES } from 'routes'

import { InfoItem } from '../InfoBlock'
import { PermissionGate } from '../../access/PermissionGate'

/**
 * The scopes THIS component can render. Derived from the copy module's scope union
 * rather than re-declared, so the two cannot end up as same-named types with
 * different members. `company` is excluded because it has no override chain and no
 * record to hang an Edit affordance on — it is the Settings -> Features card, a
 * different surface.
 */
export type AccessSettingScope = Exclude<AccessSettingCopyScope, 'company'>

interface AccessSettingItemProps {
  setting: AccessSettingKey
  /** This record's own override. `null` = inherit. */
  own: boolean | null | undefined
  /** The product's override — only meaningful for `scope="certificate"`. */
  product?: boolean | null
  /** The company's Settings -> Features toggle. */
  company: boolean | null | undefined
  scope: AccessSettingScope
  /** Needed to link "From the product" at certificate scope. */
  productId?: string
  /** `undefined` while loading — `InfoItem` renders its own skeleton for that. */
  loading?: boolean
  onEdit: () => void
}

/**
 * VRG-591 — ONE access-setting row, shared by the product and certificate detail
 * pages. Deliberately modelled on `ActivationTriggerItem`, which solved the same
 * problem for the activation trigger: resolved value + where it came from + an
 * edit affordance, in a single InfoItem cell.
 *
 * ## Why a row and not a kebab item
 *
 * An inherited setting has to show its RESOLVED VALUE and its SOURCE, because
 * "Required" alone does not tell an operator whether they are looking at a
 * deliberate choice for this record or at the company default leaking through —
 * and those call for different actions. A kebab hides the value entirely: you
 * would open a menu and then a modal just to learn whether email collection is on.
 * It would also diverge from the activation pattern this is explicitly mirroring.
 *
 * ## Why ONE COMPONENT PER FLAG rather than one bundled "Access settings" row
 *
 * The two flags resolve INDEPENDENTLY — `collect_email` may come from the product
 * while `collect_location` comes from the company, and the sources genuinely
 * differ. A bundled row can show one source or the other, never both, so it would
 * either lie or drop the provenance that is the point of the row. Two rows also
 * keep the single-line rhythm of the InfoItems beside them, and make the
 * independence visible in the UI rather than only in the schema.
 */
export const AccessSettingItem = ({
  setting,
  own,
  product,
  company,
  scope,
  productId,
  loading,
  onEdit,
}: AccessSettingItemProps) => {
  const theme = useTheme()
  // Carries "← Back to …" so a jump to Features or the product can return (§42).
  const referrer = useReferrerState()

  const option = accessSettingOption(setting)
  const Icon = option.icon

  // The system default is TRUE for both flags — see the resolver's docstring for
  // why that agrees with the schema rather than being a preference.
  const resolved = resolveAccessSetting(
    scope === 'certificate'
      ? { certificate: own, product, company }
      : { product: own, company },
    true,
  )

  /**
   * Where the value came from, and a way to GET there.
   *
   * `Default` and `Company` both read "Company default" and both link to Settings
   * -> Features, matching `ActivationTriggerItem`'s reasoning: the distinction
   * between "nobody ever chose" and "someone chose this" is real but is an
   * engineer's. The question an operator has is "where do I change this", and the
   * company page answers it either way.
   */
  const sourceLink: Partial<Record<AccessSettingSource, string>> = {
    [AccessSettingSource.Company]: ROUTES.SETTINGS_COMPANY_FEATURES,
    [AccessSettingSource.Default]: ROUTES.SETTINGS_COMPANY_FEATURES,
    ...(scope === 'certificate' && productId
      ? { [AccessSettingSource.Product]: ROUTES.PRODUCT(productId) }
      : {}),
  }

  const sourceLabel: Record<AccessSettingSource, string> = {
    [AccessSettingSource.Certificate]: 'Set on this certificate',
    [AccessSettingSource.Product]:
      scope === 'product' ? 'Set on this product' : 'From the product',
    [AccessSettingSource.Company]: 'Company default',
    [AccessSettingSource.Default]: 'Company default',
  }

  const title = resolved.value ? option.onTitle : option.offTitle

  return (
    <InfoItem
      label={option.label}
      /*
        The `ⓘ`, glossary-backed, exactly as `ActivationTriggerItem` does it one row
        up — same prop, same component, so the rows in this card read as one set
        instead of three-with-help and two-without.

        A `term` id and NOT a literal `tooltip` string. The definition lives once in
        `docs/glossary.json` and every surface reads it (§44): the tooltip body IS
        the entry's `short`, verbatim, and the footer deep-links to the full entry.
        Typing a sentence here would be a second definition that nothing compares
        against the first — and a tooltip is the worst place for that drift, since
        it is read at the moment of confusion by someone with no way to tell it is
        stale.
      */
      term={
        setting === 'collect_email' ? 'email-requirement' : 'location-requirement'
      }
      value={
        loading ? undefined : (
          /*
            ONE LINE: "[icon] Required · Company default", under a label that
            names the subject ("Require email to view").

            The same shape as ActivationTriggerItem, for the same reasons recorded
            there: value and source stacked put two lines of type into a row sized
            for one and implied the source was a fact of equal standing rather than
            an aside about where the value came from. A middot already says
            "related but subordinate".
          */
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flexWrap: 'wrap', rowGap: 1 }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Icon
                size={16}
                strokeWidth={1}
                color={theme.palette.text.secondary}
                style={{ flexShrink: 0 }}
              />
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ lineHeight: 1.25 }}
              >
                {title}
              </Typography>
              <Typography
                component="span"
                aria-hidden
                variant="body3"
                color="text.secondary"
                sx={{ lineHeight: 1.25 }}
              >
                ·
              </Typography>
              {sourceLink[resolved.source] ? (
                <Link
                  component={RouterLink}
                  to={sourceLink[resolved.source] as string}
                  state={referrer}
                  variant="body3"
                  color="text.secondary"
                  // Underline on hover only — at rest the source is context, and a
                  // permanent underline gives a secondary aside the loudest
                  // affordance in the row.
                  sx={{
                    lineHeight: 1.25,
                    textDecoration: 'none',
                    textDecorationColor: 'inherit',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {sourceLabel[resolved.source]}
                </Link>
              ) : (
                <Typography
                  variant="body3"
                  color="text.secondary"
                  sx={{ lineHeight: 1.25 }}
                >
                  {sourceLabel[resolved.source]}
                </Typography>
              )}
            </Stack>
            {/*
              GATED BY LEVEL, not by one permission for both scopes.

              | scope | permission |
              |---|---|
              | product | `manage_product` |
              | certificate | `certificate_access_settings` |

              (The company level — Settings -> Features — keeps whatever gate that
              page already has; it is a different surface and is not this
              component's business.)

              ⚠️ This is a deliberate product decision that CONTRADICTS the
              rationale written into `PermissionName.CertificateAccessSettings`,
              which argues against `manage_product` on the grounds that every role
              able to rename a product holds it. That argument is not wrong about
              the mechanics; it was overruled on the product side, where the
              override is treated as part of managing the product.

              (This note used to warn that the API's product route still carried
              `certificate_access_settings`, so a role holding `manage_product`
              alone would see an ENABLED Edit whose save 403'd. That is CLOSED:
              81762b4f0 moved the route to `@Permissions(ManageProduct)`, and the
              three grants — `manage_product`, `manage_certificate` and
              `certificate_access_settings` — are at parity. The warning is deleted
              rather than left standing against code that contradicts it.)

              `PermissionGate`'s default `disable` mode shows the control with a
              tooltip naming the permission rather than hiding it: this is an ACTION
              on a surface the user can already see, and the resolution is owned by
              an admin, asynchronously (§27/§46). Record-state unavailability would
              hide; a missing permission does not.
            */}
            <PermissionGate
              permission={
                scope === 'product' ? 'manage_product' : 'certificate_access_settings'
              }
            >
              <InlineEditButton
                label="Edit"
                onClick={onEdit}
                aria-label={`Edit the ${option.noun.toLowerCase()} for this ${scope}`}
              />
            </PermissionGate>
          </Stack>
        )
      }
    />
  )
}
